import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { PrazoPagamento } from '../../../core/models/prazos-pagamento/prazo-pagamento';

/**
 * Conteúdo (corpo) do `<ng-template #adicionarItensCampanha let-modal>` do componente pai
 * (pedido-adicionar-itens-modal.component.html:151-474 na árvore atual — os limites citados no
 * brief original, linha 1175, estavam defasados pelas extrações das Tasks 3-7; reancorado via
 * `grep -n "adicionarItensCampanha"`).
 *
 * Escopo: SOMENTE o `modal-body` (grid de produtos vinculados do combo/campanha, controle de
 * qtde geral, seletor de bonificação e filtro de produtos vinculados quando > 10 itens). O
 * `<ng-template>`, `#adicionarItensCampanha`, `modal-header` (142-150), `modal-footer` (475-478)
 * e o método abridor `modalAdicionarItensCampanha(content, id_campanha)` (ts:5366) permanecem
 * intocados no pai — os botões Cancelar/Salvar do rodapé só chamam `modal.close(...)`, não valia
 * a pena extrair.
 *
 * Nenhuma lógica de negócio de combo/bonificação foi duplicada aqui: `produtoCampanhaSelecionado`
 * é passado por referência (mesmo objeto do pai) e mutado diretamente pelos `[(ngModel)]` de
 * quantidade (linhas 306/441 do html original) — o pai enxerga a mutação porque é o mesmo objeto,
 * sem necessidade de @Output para isso. Toda ação que dispara recálculo/validação no pai
 * (incrementar/decrementar, validar qtde, alterar bonificação, filtrar) é reemitida via @Output
 * com o EXATO mesmo argumento que o template original passava ao método do pai — inclusive
 * `qtdeDecrementada`/`qtdeIncrementada`, que emitem `id_campanha` mesmo esse argumento sendo
 * ignorado por `decrementarQtdeCampanha()`/`incrementarQtdeCampanha()` (ambos sem parâmetros,
 * ts:1372/1474) — preservado assim para não alterar a assinatura de chamada original.
 */
@Component({
  selector: 'app-pedido-itens-modal-campanha',
  standalone: false,
  templateUrl: './pedido-itens-modal-campanha.component.html',
  styleUrls: ['./pedido-itens-modal-campanha.component.scss']
})
export class PedidoItensModalCampanhaComponent {
  /* Objeto principal — mesma referência do pai (ts:164: `produtoCampanhaSelecionado: ProdutoPromocional = null`). */
  @Input() produtoCampanhaSelecionado: ProdutoPromocional = null;

  /* Categoria fixa — espelha ts:63 (`combo: string = "COMBO"`), mesmo precedente do catálogo (Task 4). */
  @Input() combo = 'COMBO';

  /* Contexto de cálculo (fator financeiro / pontuação) — mesmos Inputs já existentes no pai (ts:71/102). */
  @Input() configuracao: Configuracao = null;
  @Input() prazoPagamentoSelecionado: PrazoPagamento = null;
  @Input() exibirPontuacao = false;

  /* Filtro de produtos vinculados (form real: ts:230/232/234, exibido quando produtos_vinculados.length > 10). */
  @Input() formFiltroProdutoCampanha: UntypedFormGroup = null;
  @Input() enviadoProdutoCampanha = false;
  @Input() filtrandoProdutoCampanha = false;

  /* Bonificação */
  @Input() bonificacaoInvalida = false;

  @Output() filtroSubmetido = new EventEmitter<void>();
  @Output() filtroCancelado = new EventEmitter<void>();

  @Output() qtdeDecrementada = new EventEmitter<number>();
  @Output() qtdeIncrementada = new EventEmitter<number>();
  @Output() qtdeValidada = new EventEmitter<void>();

  @Output() qtdeProdutoDecrementada = new EventEmitter<number>();
  @Output() qtdeProdutoIncrementada = new EventEmitter<number>();
  @Output() qtdeProdutoValidada = new EventEmitter<number>();

  /** Mesmo nome/tipo já usado em `pedido-itens-detalhe-painel.component.ts:66` para o mesmo
   *  método `alterarBonificacao(id_produto: string)` (ts:1693) — já wireado no pai (html:129). */
  @Output() bonificacaoAlterada = new EventEmitter<string>();
}
